import * as XLSX from 'xlsx';
import styles from './excel-download-button.module.scss';
import { Button, Icon, ICONS_ENUM } from '@sanlam-glacier/app-common/components';
import { displayAsyncToast } from '@sanlam-glacier/app-common/modules/toasts';

interface ExcelExportProps<T> {
  data: T[];
  fileName: string;
}

export const ExcelDownloadButton = <T,>({ data, fileName }: ExcelExportProps<T>) => {
  const downloadExcel = async () => {
    return new Promise<void>((resolve, reject) => {
      try {
        if (!data || data.length === 0) {
          resolve();
          return;
        }

        const worksheet = XLSX.utils.json_to_sheet(data);
        const headers = Object.keys(data[0] ?? {});
        const totalRow: Record<string, unknown> = {};

        headers.forEach((header) => {
          if (header.toLowerCase().includes('value') || header.toLowerCase().includes('amount')) {
            const sum = data.reduce((acc, row) => {
              const value = Number((row as Record<string, unknown>)[header]) || 0;
              return acc + value;
            }, 0);
            totalRow[header] = sum;
          } else if (header.toLowerCase() === 'client') {
            totalRow[header] = 'Total';
          } else {
            totalRow[header] = '';
          }
        });

        XLSX.utils.sheet_add_json(worksheet, [totalRow], { skipHeader: true, origin: -1 });
        const workbook = XLSX.utils.book_new();
        workbook.SheetNames.push('Sheet1');
        workbook.Sheets['Sheet1'] = worksheet;
        XLSX.writeFile(workbook, `${fileName}.xlsx`);
        resolve();
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleDownloadClick = () => {
    displayAsyncToast({
      toastId: `excel-download-${fileName}`,
      title: 'Excel sheet downloading...',
      message:
        'Excel sheet will be available in your downloads folder when the download is complete.',
      successMessage: 'Excel sheet successfully downloaded.',
      errorMessage: 'Excel sheet download failed.',
      promiseCompleteTitle: 'Excel document download',
      promise: downloadExcel
    });
  };

  return (
    <Button
      onClick={handleDownloadClick}
      className={styles.excelDownloadButton}
      startIcon={<Icon icon={ICONS_ENUM.DOCUMENTS_EXCEL_DOCUMENT} />}
      variant={Button.VARIANT.SECONDARY}
      size={Button.SIZE.MEDIUM}
      data-testid='excelDownloadButton'
    >
      Download Excel
    </Button>
  );
};

export default ExcelDownloadButton;
